import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "../api";
import { toast } from "sonner";
import { CalendarDays, Filter, X as XIcon, Users, Tag, Trophy, ListTree, BarChart3 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const EVENT_LABELS = {
  attend_start: "Iniciou atendimento",
  attend_end: "Finalizou atendimento",
  pass_turn: "Passou a vez",
  start_shift: "Iniciou expediente",
  end_shift: "Encerrou expediente",
  pause: "Pausou",
  resume: "Voltou da pausa",
  manual_move: "Movimentação manual da fila",
  call_started: "Chamada enviada",
  call_accepted: "Aceitou a chamada",
  call_declined: "Recusou a chamada",
  call_timeout: "Não respondeu à chamada",
  call_cancelled: "Chamada cancelada",
  call_replay: "Chamada repetida na TV",
  admin_created: "Cadastrou recepcionista",
  admin_updated: "Editou recepcionista",
  admin_deleted: "Excluiu recepcionista",
  tv_pin_updated: "Alterou PIN do Painel TV",
  call_settings_updated: "Alterou configurações de chamada",
};

const EVENT_GROUPS = [
  { label: "Expediente", types: ["start_shift", "end_shift", "pause", "resume"] },
  { label: "Atendimento", types: ["attend_start", "attend_end", "pass_turn"] },
  { label: "Chamadas", types: ["call_started", "call_accepted", "call_declined", "call_timeout", "call_cancelled", "call_replay"] },
  { label: "Fila (manual)", types: ["manual_move"] },
  { label: "Administração", types: ["admin_created", "admin_updated", "admin_deleted", "tv_pin_updated", "call_settings_updated"] },
];

function pad(n) { return String(n).padStart(2, "0"); }
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function shiftDays(dateStr, delta) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}
function startOfMonth(dateStr) {
  const [y, m] = dateStr.split("-").map(Number);
  return `${y}-${pad(m)}-01`;
}
function endOfMonth(dateStr) {
  const [y, m] = dateStr.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return `${y}-${pad(m)}-${pad(last)}`;
}
function prevMonthRange(dateStr) {
  const [y, m] = dateStr.split("-").map(Number);
  const prev = new Date(y, m - 2, 1);
  const py = prev.getFullYear();
  const pm = prev.getMonth() + 1;
  const last = new Date(py, pm, 0).getDate();
  return { from: `${py}-${pad(pm)}-01`, to: `${py}-${pad(pm)}-${pad(last)}` };
}
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fmtDuration(sec) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${pad(s)}s`;
}
function fmtDurationShort(sec) {
  if (!sec) return "0m";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${pad(m)}m`;
  return `${m}m`;
}

const PRESETS = [
  { key: "today", label: "Hoje" },
  { key: "yesterday", label: "Ontem" },
  { key: "7d", label: "7 dias" },
  { key: "this_month", label: "Este mês" },
  { key: "prev_month", label: "Mês anterior" },
  { key: "custom", label: "Personalizado" },
];

function presetRange(key) {
  const today = todayStr();
  if (key === "today") return { from: today, to: today };
  if (key === "yesterday") { const y = shiftDays(today, -1); return { from: y, to: y }; }
  if (key === "7d") return { from: shiftDays(today, -6), to: today };
  if (key === "this_month") return { from: startOfMonth(today), to: today };
  if (key === "prev_month") return prevMonthRange(today);
  return { from: today, to: today };
}

function KpiCard({ label, value, hint, testid, color = "text-black" }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`mt-1 font-display text-3xl font-semibold ${color}`}>{value}</div>
      {hint && <div className="mt-1 text-[11px] text-gray-500">{hint}</div>}
    </div>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // Filtros gerais
  const [preset, setPreset] = useState("today");
  const initial = presetRange("today");
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [catFilter, setCatFilter] = useState("");
  const [profFilter, setProfFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // Filtros auditoria (independentes dos filtros gerais na parte de tipos e actor)
  const [actorFilter, setActorFilter] = useState("");
  const [eventGroupSel, setEventGroupSel] = useState({}); // {label: true}

  // Tab
  const [tab, setTab] = useState("attendances"); // "attendances" | "audit"
  const [rankMode, setRankMode] = useState("professional"); // "professional" | "category"
  const [rankSort, setRankSort] = useState("total"); // "total"|"vez"|"preferencia"|"avg_per_day"|"avg_seconds"

  // Data
  const [cats, setCats] = useState([]);
  const [profs, setProfs] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
    if (isAdmin) {
      api.get("/professionals").then(({ data }) => setProfs(data)).catch(() => {});
      api.get("/admins").then(({ data }) => setAdmins(data)).catch(() => {});
    }
  }, [isAdmin]);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== "custom") {
      const r = presetRange(key);
      setDateFrom(r.from);
      setDateTo(r.to);
    }
  };

  const activeEventTypes = useMemo(() => {
    const chosen = Object.entries(eventGroupSel).filter(([, v]) => v).map(([k]) => k);
    if (chosen.length === 0) return "";
    const types = new Set();
    for (const g of EVENT_GROUPS) if (chosen.includes(g.label)) g.types.forEach((t) => types.add(t));
    return Array.from(types).join(",");
  }, [eventGroupSel]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from: dateFrom, to: dateTo };
      if (catFilter) params.category_id = catFilter;
      if (profFilter) params.professional_id = profFilter;
      if (typeFilter) params.type = typeFilter;
      if (actorFilter) params.actor_id = actorFilter;
      if (activeEventTypes) params.event_types = activeEventTypes;
      const { data } = await api.get("/reports", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, catFilter, profFilter, typeFilter, actorFilter, activeEventTypes]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setCatFilter(""); setProfFilter(""); setTypeFilter("");
    setActorFilter(""); setEventGroupSel({});
    applyPreset("today");
  };

  const rankRows = useMemo(() => {
    if (!data) return [];
    const rows = [...(rankMode === "professional" ? data.ranking_by_professional : data.ranking_by_category)];
    rows.sort((a, b) => (b[rankSort] || 0) - (a[rankSort] || 0));
    return rows;
  }, [data, rankMode, rankSort]);

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gray-500">Análise</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Relatórios</h1>
        </div>
        <button
          data-testid="btn-clear-filters"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          <XIcon size={12} /> Limpar filtros
        </button>
      </div>

      {/* 1) FILTROS GERAIS */}
      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gray-500">
          <Filter size={12} /> Filtros
        </div>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="preset-chips">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              data-testid={`preset-${p.key}`}
              onClick={() => applyPreset(p.key)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                preset === p.key ? "border-black bg-black text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">De</label>
            <div className="mt-1 inline-flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs">
              <CalendarDays size={14} className="text-gray-400" />
              <input
                data-testid="input-from"
                type="date"
                value={dateFrom}
                max={dateTo || todayStr()}
                onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }}
                className="w-full bg-transparent outline-none [color-scheme:light]"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Até</label>
            <div className="mt-1 inline-flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs">
              <CalendarDays size={14} className="text-gray-400" />
              <input
                data-testid="input-to"
                type="date"
                value={dateTo}
                min={dateFrom}
                max={todayStr()}
                onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }}
                className="w-full bg-transparent outline-none [color-scheme:light]"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Categoria</label>
            <select
              data-testid="select-category"
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-black"
            >
              <option value="">Todas</option>
              {cats.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          {isAdmin && (
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Profissional</label>
              <select
                data-testid="select-professional"
                value={profFilter}
                onChange={(e) => setProfFilter(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-black"
              >
                <option value="">Todos</option>
                {profs.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
            </div>
          )}
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Tipo de atendimento</label>
            <select
              data-testid="select-type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-black"
            >
              <option value="">Todos</option>
              <option value="vez">Vez</option>
              <option value="preferencia">Preferência</option>
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="mb-4 text-xs uppercase tracking-widest text-gray-400">Carregando…</div>}

      {/* 2) RESUMO */}
      {data && (
        <div className="mb-8">
          <h2 className="mb-3 font-display text-xl font-semibold tracking-tight">Resumo</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard testid="kpi-total" label="Total" value={data.summary.total} hint={`${data.num_days} dia(s)`} />
            <KpiCard testid="kpi-vez" label="Vez" value={data.summary.vez} />
            <KpiCard testid="kpi-preferencia" label="Preferência" value={data.summary.preferencia} color="text-pink-600" />
            <KpiCard testid="kpi-avg-per-day" label="Média por dia" value={data.summary.avg_per_day} hint="atendimentos/dia" />
            <KpiCard testid="kpi-avg-time" label="Tempo médio" value={fmtDurationShort(data.summary.avg_seconds)} hint="por atendimento" />
            <KpiCard testid="kpi-no-response" label="Passou/Não respondeu" value={data.summary.no_response} hint={`${data.summary.pass_turn} passou · ${data.summary.call_timeout} timeout`} color="text-amber-600" />
          </div>
        </div>
      )}

      {/* 3 e 4: Abas */}
      <div className="mb-4 inline-flex overflow-hidden rounded-full border border-gray-200 bg-white" data-testid="tabs">
        <button
          data-testid="tab-attendances"
          onClick={() => setTab("attendances")}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-widest ${
            tab === "attendances" ? "bg-black text-white" : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <BarChart3 size={12} /> Atendimentos + Ranking
        </button>
        <button
          data-testid="tab-audit"
          onClick={() => setTab("audit")}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-widest ${
            tab === "audit" ? "bg-black text-white" : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <ListTree size={12} /> Movimentações / Auditoria
        </button>
      </div>

      {tab === "attendances" && data && (
        <div className="space-y-8">
          {/* Ranking */}
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-xl font-semibold tracking-tight inline-flex items-center gap-2">
                <Trophy size={18} className="text-amber-500" /> Ranking
              </h2>
              <div className="flex flex-wrap gap-2">
                <div className="inline-flex overflow-hidden rounded-full border border-gray-200">
                  <button
                    data-testid="rank-by-professional"
                    onClick={() => setRankMode("professional")}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest ${rankMode==="professional" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                  >
                    <Users size={12} /> Profissional
                  </button>
                  <button
                    data-testid="rank-by-category"
                    onClick={() => setRankMode("category")}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest ${rankMode==="category" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                  >
                    <Tag size={12} /> Categoria
                  </button>
                </div>
                <select
                  data-testid="rank-sort"
                  value={rankSort}
                  onChange={(e) => setRankSort(e.target.value)}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 outline-none"
                >
                  <option value="total">Ordenar: Total</option>
                  <option value="vez">Ordenar: Vez</option>
                  <option value="preferencia">Ordenar: Preferência</option>
                  <option value="avg_per_day">Ordenar: Média/dia</option>
                  <option value="avg_seconds">Ordenar: Tempo médio</option>
                </select>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-widest text-gray-500">
                  <tr>
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">{rankMode === "professional" ? "Profissional" : "Categoria"}</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Vez</th>
                    <th className="px-4 py-3">Pref.</th>
                    <th className="px-4 py-3">Média/dia</th>
                    <th className="px-4 py-3">Tempo médio</th>
                  </tr>
                </thead>
                <tbody>
                  {rankRows.length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-gray-400">Sem dados no período</td></tr>
                  )}
                  {rankRows.map((r, i) => (
                    <tr key={r.id} data-testid={`rank-row-${r.id}`} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                      <td className="px-4 py-3 font-medium">{r.name}</td>
                      <td className="px-4 py-3 font-display text-base text-pink-600">{r.total}</td>
                      <td className="px-4 py-3">{r.vez}</td>
                      <td className="px-4 py-3">{r.preferencia}</td>
                      <td className="px-4 py-3">{r.avg_per_day}</td>
                      <td className="px-4 py-3">{fmtDurationShort(r.avg_seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabela de Atendimentos */}
          <div>
            <h2 className="mb-3 font-display text-xl font-semibold tracking-tight">Atendimentos</h2>
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-widest text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Profissional</th>
                    <th className="px-4 py-3">Categoria</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">Início</th>
                    <th className="px-4 py-3">Fim</th>
                    <th className="px-4 py-3">Duração</th>
                  </tr>
                </thead>
                <tbody>
                  {data.attendances.length === 0 && (
                    <tr><td colSpan={6} className="py-10 text-center text-gray-400">Nenhum atendimento no período</td></tr>
                  )}
                  {data.attendances.map((a) => (
                    <tr key={a.id} className="border-t border-gray-100" data-testid={`att-row-${a.id}`}>
                      <td className="px-4 py-3 font-medium">{a.professional_name}</td>
                      <td className="px-4 py-3">{a.category_name}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${a.type === "vez" ? "bg-black text-white" : "bg-pink-100 text-pink-700"}`}>
                          {a.type === "vez" ? "VEZ" : "PREFERÊNCIA"}
                        </span>
                      </td>
                      <td className="px-4 py-3">{fmtDateTime(a.started_at)}</td>
                      <td className="px-4 py-3">{fmtDateTime(a.ended_at)}</td>
                      <td className="px-4 py-3">{fmtDuration(a.duration_seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "audit" && data && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="mb-3 text-[11px] uppercase tracking-[0.25em] text-gray-500">Filtros de auditoria</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {isAdmin && (
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Usuário responsável (recepção)</label>
                  <select
                    data-testid="select-actor"
                    value={actorFilter}
                    onChange={(e) => setActorFilter(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-black"
                  >
                    <option value="">Todos</option>
                    {admins.map((a) => (<option key={a.id} value={a.id}>{a.name}{a.admin_level === "master" ? " · Master" : ""}</option>))}
                  </select>
                </div>
              )}
              <div className="md:col-span-2">
                <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Tipos de movimentação</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {EVENT_GROUPS.map((g) => {
                    const active = !!eventGroupSel[g.label];
                    return (
                      <button
                        key={g.label}
                        data-testid={`evt-group-${g.label.toLowerCase().replace(/\s+/g,'-')}`}
                        onClick={() => setEventGroupSel((s) => ({ ...s, [g.label]: !active }))}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium ${active ? "border-black bg-black text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}
                      >
                        {g.label}
                      </button>
                    );
                  })}
                  {(Object.values(eventGroupSel).some(Boolean)) && (
                    <button
                      data-testid="btn-clear-evt-groups"
                      onClick={() => setEventGroupSel({})}
                      className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50"
                    >
                      Limpar
                    </button>
                  )}
                </div>
                <div className="mt-2 text-[11px] text-gray-500">
                  Sem seleção = mostra todos os tipos. O filtro de profissional e período no topo também se aplicam aqui.
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            {data.events.length === 0 ? (
              <div className="py-8 text-center text-gray-400">Nenhuma movimentação com esses filtros</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {data.events.map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] uppercase tracking-widest text-gray-600">
                        {EVENT_LABELS[e.type] || e.type}
                      </span>
                      {e.professional_name && <span className="font-medium">{e.professional_name}</span>}
                      {e.category_name && <span className="text-gray-500">· {e.category_name}</span>}
                      {e.reason_label && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-800">
                          {e.reason_label}
                        </span>
                      )}
                      {e.actor_name && e.actor_id && e.actor_id !== e.professional_id && (
                        <span
                          data-testid={`event-actor-${e.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-pink-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-pink-700"
                        >
                          {e.actor_role === "admin" ? "Recepção" : "Por"}: {e.actor_name}
                        </span>
                      )}
                      {e.meta?.note && <span className="text-xs italic text-gray-500">“{e.meta.note}”</span>}
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(e.timestamp)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
